import { AuditRow } from "../models/AuditRow";
import { classKmTotal, orderedUnitTotals, PeriodSummary, SplitBasis } from "./periodSummary";
import { FUEL_TYPE_UNITS } from "@/constants/FuelTypeGroups";
import { ExportColumn, ExportTable } from "@/utilities/export/table";
import { WorkbookCell } from "@/utilities/xlsx";

/**
 * Restricts the export to the columns that are visible in the grid, in grid order. Columns
 * flagged as not exportable are dropped regardless of visibility.
 */
export function visibleExportColumns(
  columns: (ExportColumn & { disableExport?: boolean })[],
  visibilityModel: Record<string, boolean>,
  orderedFields: string[],
): ExportColumn[] {
  const byField = new Map(columns.map(column => [column.field, column]));
  return orderedFields
    .map(field => byField.get(field))
    .filter((column): column is ExportColumn & { disableExport?: boolean } => !!column)
    .filter(column => !column.disableExport && visibilityModel[column.field] !== false)
    .map(({ field, header, fractionDigits }) => ({ field, header, fractionDigits }));
}

/**
 * Builds the audit table: one line per day summary and per trip detail. The first column states
 * the level so the two can be told apart in Excel.
 */
export function buildAuditTable(
  columns: ExportColumn[],
  rows: AuditRow[],
  getCell: (row: AuditRow, field: string) => WorkbookCell,
  levelColumn: { header: string; day: string; trip: string },
): ExportTable {
  return {
    columns: [{ field: "level", header: levelColumn.header }, ...columns],
    rows: rows.map(row => [
      row.rowType === "day" ? levelColumn.day : levelColumn.trip,
      ...columns.map(column => getCell(row, column.field)),
    ]),
  };
}

export interface SummaryExportLabels {
  vehicle: string;
  kmTotal: string;
  kmFavored: string;
  kmNotFavored: string;
  consumption: string;
  refuel: string;
  consumptionFavored: string;
  consumptionNotFavored: string;
  consumptionNone: string;
  /** Header of the column naming what the split divides, and its two values. */
  splitBasis: string;
  splitBasisValues: Record<SplitBasis, string>;
  /** Header of the unit column (l, kWh) that qualifies the consumption figures of a line. */
  unit: string;
  total: string;
}

const SUMMARY_FIGURE_DIGITS = 1;

/**
 * The "Zusammenfassung" table: one line per vehicle with the kilometres of the booked entries
 * (total, favored, not favored), consumption and fill-ups as numbers, the consumption split per
 * tax class with its basis when the period allows it, followed by a total line per unit. Figures
 * are plain numbers; the unit column says whether a line is litres or kWh.
 */
export function buildSummaryTable(
  summary: PeriodSummary,
  labels: SummaryExportLabels,
  includeSplit: boolean,
): ExportTable {
  const figure = (field: string, header: string): ExportColumn => ({
    field,
    header,
    fractionDigits: SUMMARY_FIGURE_DIGITS,
  });
  const columns: ExportColumn[] = [
    { field: "vehicle", header: labels.vehicle },
    { field: "unit", header: labels.unit },
    figure("kmTotal", labels.kmTotal),
    figure("kmFavored", labels.kmFavored),
    figure("kmNotFavored", labels.kmNotFavored),
    figure("consumption", labels.consumption),
    figure("refuel", labels.refuel),
    ...(includeSplit
      ? [
          figure("consumptionFavored", labels.consumptionFavored),
          figure("consumptionNotFavored", labels.consumptionNotFavored),
          figure("consumptionNone", labels.consumptionNone),
          { field: "splitBasis", header: labels.splitBasis },
        ]
      : []),
  ];
  const body: WorkbookCell[][] = summary.vehicles.map(vehicle => [
    vehicle.device.name,
    vehicle.device.fuelType ? FUEL_TYPE_UNITS[vehicle.device.fuelType] : "",
    classKmTotal(vehicle.km),
    vehicle.km.favored,
    vehicle.km.notFavored,
    vehicle.consumption ?? null,
    vehicle.refuelLiters ?? null,
    ...(includeSplit
      ? [
          vehicle.split?.figures.favored ?? null,
          vehicle.split?.figures.notFavored ?? null,
          vehicle.split?.figures.none ?? null,
          vehicle.split ? labels.splitBasisValues[vehicle.split.basis] : "",
        ]
      : []),
  ]);
  // One total line per unit, since litres and kWh cannot be added; the kilometres go on the first.
  const totalRows: WorkbookCell[][] = orderedUnitTotals(summary.totals).map(([unit, bucket], index) => [
    labels.total,
    unit === "unknown" ? "" : FUEL_TYPE_UNITS[unit],
    index === 0 ? classKmTotal(summary.totals.km) : null,
    index === 0 ? summary.totals.km.favored : null,
    index === 0 ? summary.totals.km.notFavored : null,
    bucket.consumption ?? null,
    bucket.refuelLiters ?? null,
    ...(includeSplit ? [bucket.split?.favored ?? null, bucket.split?.notFavored ?? null, bucket.split?.none ?? null, ""] : []),
  ]);
  return { columns, rows: [...body, ...totalRows] };
}
